import React, { useState, useEffect } from "react";
import { Table, Button, Container } from "react-bootstrap";
import { ApiGet, ApiPost, ApiPut, ApiDel } from "../../../service/ApiService";
import { formatDate } from "../../../service/DateUtils";
import TenantModal from "./TenantModal"; // Import the extracted modal component

const Tenants = () => {
    const [tenants, setTenants] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [currentTenant, setCurrentTenant] = useState(null);
    const [formData, setFormData] = useState({ name: "", email: "" });
    const [error, setError] = useState(null);

    // Fetch tenants from the API
    useEffect(() => {
        fetchTenants();
    }, []);

    const fetchTenants = () => {
        setError(null);
        ApiGet(
            "/api/tenant/list",
            (data) => setTenants(data),
            (error) => setError(error)
        );
    };

    // Handle form input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    // Handle add/edit tenant
    const handleSaveTenant = () => {
        setShowModal(false);
        setError(null);

        if (currentTenant) {
            // Edit tenant
            ApiPut(
                `/api/tenant/${currentTenant.id}`,
                formData,
                () => {
                    fetchTenants();
                    setShowModal(false);
                    setFormData({ name: "", email: "" });
                    setCurrentTenant(null);
                },
                (error) => setError(error)
            );
        } else {
            // Add tenant
            ApiPost(
                "/api/tenant/add",
                formData,
                () => {
                    fetchTenants();
                    setShowModal(false);
                    setFormData({ name: "", email: "" });
                },
                (error) => setError(error)
            );
        }
    };

    // Handle delete tenant with confirmation
    const handleDeleteTenant = (id) => {
        setError(null);

        const confirmDelete = window.confirm("Are you sure you want to delete this tenant?");
        if (confirmDelete) {
            ApiDel(
                `/api/tenant/${id}`,
                () => fetchTenants(),
                (error) => setError(error)
            );
        }
    };

    // Open modal for adding or editing a tenant
    const openModal = (tenant = null) => {
        setCurrentTenant(tenant);
        setFormData(tenant || { name: "", email: "" });
        setShowModal(true);
    };

    return (
        <Container fluid className="mt-5">
            <h2 className="mb-2">Tenants Management</h2>

            <div className="d-flex justify-content-between mb-3">
                <div></div> {/* Empty div to push the button to the right */}
                <Button
                    variant="primary"
                    className="d-flex align-items-center"
                    onClick={() => openModal()}
                >
                    Add Tenant
                </Button>
            </div>

            {error && <div className="alert alert-danger">Error: {error.message}</div>}
            <Table striped bordered hover>
                <thead>
                    <tr>
                        <th>#</th>
                        <th>Name</th>
                        <th>Description</th>
                        <th>Created</th>
                        <th>Updated</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {!!tenants &&
                        tenants.map &&
                        tenants.map((tenant, index) => (
                            <tr key={tenant.id}>
                                <td>{tenant.id}</td>
                                <td><strong>{tenant.name}</strong></td>
                                <td>{tenant.description}</td>
                                <td>{formatDate(tenant.createdAt)}</td>
                                <td>{formatDate(tenant.updatedAt)}</td>
                                <td>
                                    {tenant.id > 1 && /* do not edit super-admin */
                                    <>
                                        <Button
                                            variant="warning"
                                            className="me-2"
                                            onClick={() => openModal(tenant)}
                                        >
                                            Edit
                                        </Button>
                                        <Button
                                            variant="danger"
                                            onClick={() => handleDeleteTenant(tenant.id)}
                                        >
                                            Delete
                                        </Button>
                                    </>}
                                </td>
                            </tr>
                        ))}
                </tbody>
            </Table>

            {/* Use the extracted TenantModal component */}
            <TenantModal
                show={showModal}
                onHide={() => setShowModal(false)}
                onSave={handleSaveTenant}
                formData={formData}
                handleInputChange={handleInputChange}
                currentTenant={currentTenant}
            />
        </Container>
    );
};

export default Tenants;
